import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, Routes } from '@angular/router';
import { catchError, EMPTY, Observable, of, switchMap } from 'rxjs';
import { BoardAdminGuard } from './core/guards/board-admin-guard.service';
import { homeRedirectGuard } from './core/guards/home-redirect.guard';
import { ToolAdminGuard } from './core/guards/tool-admin-guard.service';
import { ForbiddenComponent } from './pages/app/forbidden/forbidden.component';
import { PageNotFoundComponent } from './pages/app/page-not-found/page-not-found.component';
import { HomeComponent } from './pages/home/home.component';
import { ItemsFilterCardResolver } from './pages/items/items-filter-card/items-filter-card.resolver';
import { ItemsFilterCardStore } from './pages/items/items-filter-card/items-filter-card.store';
import { ItemsComponent } from './pages/items/items.component';
import { IItemBase, IItemJoins } from './pages/items/items.interface';
import { ItemsResolver } from './pages/items/items.resolver';
import { ItemsService } from './pages/items/items.service';
import { BoardsComponent } from './pages/settings/boards/boards.component';
import { CategoriesComponent } from './pages/settings/categories/categories.component';
import { FieldsFieldSetsComponent } from './pages/settings/fields-field-sets/fields-field-sets.component';
import { UsersTeamsComponent } from './pages/settings/users-teams/users-teams.component';
import { WorkInstructionsComponent } from './pages/settings/work-instructions/work-instructions.component';
import { WorkflowManagementComponent } from './pages/settings/workflow-management/workflow-management.component';
import { KanbanViewsComponent } from './pages/settings/kanban-views/kanban-views.component';
import { StandaloneItemComponent } from './pages/standalone-item/standalone-item.component';

export const routes: Routes = [
  {
    canActivate: [homeRedirectGuard],
    component: HomeComponent,
    outlet: '',
    path: 'home',
    pathMatch: 'full',
  },
  {
    component: ItemsComponent,
    data: { icon: 'format_list_bulleted', showFilterButton: true, showFilterCard: true, title: 'page.items.title' },
    outlet: '',
    path: 'items',
    pathMatch: 'full',
    providers: [ItemsFilterCardStore, ItemsFilterCardResolver],
    resolve: {
      boardItemsInitialData: (route: ActivatedRouteSnapshot) => inject(ItemsResolver).resolve(route),
      filterCardManager: (route: ActivatedRouteSnapshot) => inject(ItemsFilterCardResolver).resolve(route),
    },
  },
  {
    path: 'items/:key',
    pathMatch: 'full',
    redirectTo: 'items/:key/all',
  },
  {
    component: ItemsComponent,
    data: { icon: 'format_list_bulleted', showFilterButton: true, showFilterCard: true, title: 'page.items.title' },
    outlet: '',
    path: 'items/:key/:scope',
    pathMatch: 'full',
    providers: [ItemsFilterCardStore, ItemsFilterCardResolver],
    resolve: {
      boardItemsInitialData: (route: ActivatedRouteSnapshot) => inject(ItemsResolver).resolve(route),
      filterCardManager: (route: ActivatedRouteSnapshot) => inject(ItemsFilterCardResolver).resolve(route),
    },
  },
  {
    component: ItemsComponent,
    data: { icon: 'format_list_bulleted', showFilterButton: true, showFilterCard: true, title: 'page.items.title' },
    outlet: '',
    path: 'items/:key/:scope/:itemKey',
    pathMatch: 'full',
    providers: [ItemsFilterCardStore, ItemsFilterCardResolver],
    resolve: {
      boardItemsInitialData: (route: ActivatedRouteSnapshot) => inject(ItemsResolver).resolve(route),
      filterCardManager: (route: ActivatedRouteSnapshot) => inject(ItemsFilterCardResolver).resolve(route),
    },
  },
  {
    component: StandaloneItemComponent,
    data: {
      appComponent: {
        backgroundColor: 'white',
      },
    },
    outlet: '',
    path: 'item/:itemKey',
    pathMatch: 'full',
    resolve: {
      item: (route: ActivatedRouteSnapshot): Observable<(IItemBase & Pick<IItemJoins, 'board'>) | never | null> => {
        const itemsService = inject(ItemsService);
        const router = inject(Router);

        const itemKey: string | null | undefined = route.paramMap.get('itemKey');

        if (!itemKey) {
          router.navigate(['not-found']);

          return EMPTY;
        }

        if (itemKey === 'create') {
          return of(null);
        }

        return itemsService.getItem(itemKey).pipe(
          switchMap((response) => {
            if (!response.data.length) {
              router.navigate(['not-found']);

              return EMPTY;
            }

            return of(response.data[0]);
          }),
          catchError(() => {
            router.navigate(['not-found']);

            return EMPTY;
          }),
        );
      },
    },
  },
  {
    children: [
      {
        canActivate: [ToolAdminGuard],
        component: UsersTeamsComponent,
        data: { icon: 'person', title: 'page.usersAndTeams.title' },
        outlet: '',
        path: 'users',
      },
      {
        canActivate: [ToolAdminGuard],
        component: FieldsFieldSetsComponent,
        data: { icon: 'format_list_bulleted', title: 'page.fields.title' },
        outlet: '',
        path: 'fields',
      },
      {
        canActivate: [BoardAdminGuard],
        component: BoardsComponent,
        data: { icon: 'window', title: 'page.boards.title' },
        outlet: '',
        path: 'boards',
      },
      {
        canActivate: [BoardAdminGuard],
        component: CategoriesComponent,
        data: { icon: 'category', title: 'page.categories.title' },
        outlet: '',
        path: 'categories',
      },
      {
        canActivate: [BoardAdminGuard],
        component: WorkInstructionsComponent,
        data: { icon: 'menu_book', title: 'page.workInstructions.title' },
        outlet: '',
        path: 'work-instructions',
      },
      {
        canActivate: [BoardAdminGuard],
        component: WorkflowManagementComponent,
        data: { icon: 'content_paste_go', title: 'page.workflowManagement.title' },
        outlet: '',
        path: 'workflows',
      },
      {
        canActivate: [ToolAdminGuard],
        component: KanbanViewsComponent,
        data: { icon: 'view_kanban', title: 'page.kanbanViews.title' },
        outlet: '',
        path: 'kanban-views',
      },
    ],
    path: 'settings',
  },
  { path: '', pathMatch: 'full', redirectTo: '/home' },
  {
    children: [
      {
        component: ForbiddenComponent,
        data: { icon: 'lock', title: 'page.forbidden.title' },
        outlet: '',
        path: 'forbidden',
      },
    ],
    path: 'error',
  },
  {
    data: { icon: 'view_timeline', title: 'page.choreography.title' },
    loadComponent: () =>
      import('./pages/dashboard/choreography/choreography.component').then((m) => m.ChoreographyComponent),
    path: 'dashboard/choreography/:itemKey',
  },
  { component: PageNotFoundComponent, data: { icon: 'find_in_page', title: 'page.notFound.title' }, path: '**' },
];
